import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { isHybridMode } from '../utils/config';

const POLL_MS = 15000;

/**
 * Polls the number of currently-open backfill slots ("Öppna platser").
 *
 * Only registered, active members can actually claim a slot, so the hook
 * short-circuits to ``count=0`` for anonymous / pending users without hitting
 * the API — entry points (start-page banner, nav badge) stay hidden for
 * everyone who couldn't act on them anyway. The server re-enforces eligibility
 * on claim, this is purely UI gating.
 *
 * @returns {{count: number, loading: boolean}}
 */
export default function useOpenSlotsCount() {
  const { isLoggedIn, isActive, getAuthHeaders } = useAuth();
  const eligible = isHybridMode && isLoggedIn && isActive;
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(eligible);

  const refresh = useCallback(async () => {
    if (!eligible) {
      setCount(0);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/open-slots/`, {
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data) ? data : data.results || [];
        setCount(rows.filter((s) => s.status === 'open').length);
      }
    } catch {
      // Best-effort poll — keep the last known count on a transient failure.
    } finally {
      setLoading(false);
    }
  }, [eligible, getAuthHeaders]);

  useEffect(() => {
    refresh();
    if (!eligible) return undefined;
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [eligible, refresh]);

  return { count, loading };
}
